import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { useEmployeeOverview } from "../EmployeeOverview/EmployeeOverviewContext";
import type { EmployeeSalaryStructure } from "../types/employee-salary-structure.types";
import {
  deleteEmployeeSalaryStructure,
  listEmployeeSalaryStructures,
} from "../services/employeeSalaryStructureService";
import SalaryStructureFormModal from "./SalaryStructureFormModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const SalaryStructuresTab = () => {
  const { can } = useAccess();
  const canCreate = can(Permissions.EMPLOYEE_SALARY_STRUCTURE.CREATE);
  const canUpdate = can(Permissions.EMPLOYEE_SALARY_STRUCTURE.UPDATE);
  const canDelete = can(Permissions.EMPLOYEE_SALARY_STRUCTURE.DELETE);
  const { employee } = useEmployeeOverview();
  const employeeId = employee?.id;

  const [items, setItems] = useState<EmployeeSalaryStructure[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeSalaryStructure | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<EmployeeSalaryStructure | null>(null);

  const load = useCallback(async () => {
    if (!employeeId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await listEmployeeSalaryStructures(employeeId);
      if (res.success) {
        setItems(res.data ?? []);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to load salary structures",
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: EmployeeSalaryStructure) => {
    setEditing(row);
    setFormOpen(true);
  };

  const confirmDelete = (row: EmployeeSalaryStructure) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!employeeId || !deleteTarget) return;
    try {
      const res = await deleteEmployeeSalaryStructure(
        employeeId,
        deleteTarget.id,
      );
      if (res.success) {
        toast.success(res.message);
        setDeleteOpen(false);
        setDeleteTarget(null);
        load();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to delete salary structure",
      );
    }
  };

  if (!employeeId) {
    return (
      <Row>
        <Col>
          <Card>
            <CardBody className="text-muted">Employee not found.</CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteOpen}
        onCloseClick={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onDeleteClick={handleDelete}
        recordId={
          deleteTarget
            ? `effective ${deleteTarget.effectiveFrom?.slice(0, 10)}`
            : undefined
        }
      />

      <SalaryStructureFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        employeeId={employeeId}
        editRecord={editing}
        onSaved={load}
      />

      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h5 className="card-title mb-0">Salary structures</h5>
                <p className="text-muted mb-0 small">
                  Pay breakdown by effective period (line-item components)
                </p>
              </div>
              {canCreate ? (
                <Button color="primary" size="sm" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  Add structure
                </Button>
              ) : null}
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : items.length === 0 ? (
                <p className="text-muted mb-0">
                  No salary structures yet. Add one with effective dates and pay
                  components for this employee.
                </p>
              ) : (
                <div className="table-responsive table-card mb-3">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead className="table-light text-muted">
                      <tr>
                        <th>Effective from</th>
                        <th>Effective to</th>
                        <th className="text-center">Components</th>
                        <th className="text-end">Net salary</th>
                        {canUpdate || canDelete ? (
                          <th className="text-end" style={{ width: "120px" }}>
                            Actions
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <DateOnly date={row.effectiveFrom} />
                          </td>
                          <td>
                            {row.effectiveTo ? (
                              <DateOnly date={row.effectiveTo} />
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-center">
                            {row.components?.length ?? 0}
                          </td>
                          <td className="text-end fw-medium">
                            {formatCurrency(Number(row.netAmount))}
                          </td>
                          {canUpdate || canDelete ? (
                            <td className="text-end">
                              <div className="hstack gap-1 justify-content-end">
                                {canUpdate ? (
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="btn-soft-primary"
                                    onClick={() => openEdit(row)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                ) : null}
                                {canDelete ? (
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="btn-soft-danger"
                                    onClick={() => confirmDelete(row)}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default SalaryStructuresTab;
